import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

type Flashcard = { id: string; front: string; reading: string | null; meaning: string; example_sentence: string | null; example_translation: string | null };
type Review = { id?: string; ease: number; interval_days: number; repetitions: number; due_at: string };

function shuffle<T>(items: T[]) {
  return [...items].sort(() => Math.random() - 0.5);
}

// SM-2 algorithm
function nextReview(r: Review, q: number): Review {
  let { ease, interval_days, repetitions } = r;
  if (q < 3) {
    repetitions = 0;
    interval_days = 1;
  } else {
    if (repetitions === 0) interval_days = 1;
    else if (repetitions === 1) interval_days = 6;
    else interval_days = Math.round(interval_days * ease);
    repetitions += 1;
    ease = Math.max(1.3, ease + (0.1 - (5 - q) * (0.08 + (5 - q) * 0.02)));
  }
  const due = new Date();
  due.setDate(due.getDate() + interval_days);
  return { ease, interval_days, repetitions, due_at: due.toISOString() };
}

export default function DeckStudy() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [deck, setDeck] = useState<any>(null);
  const [allCards, setAllCards] = useState<Flashcard[]>([]);
  const [queue, setQueue] = useState<{ card: Flashcard; review: Review }[]>([]);
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: d } = await supabase.from("flashcard_decks").select("*").eq("slug", slug).maybeSingle();
      if (!d) return;
      setDeck(d);
      const { data: cards } = await supabase.from("flashcards").select("*").eq("deck_id", d.id).order("sort_order");
      const cardList = (cards || []) as Flashcard[];
      setAllCards(cardList);
      const { data: revs } = await supabase.from("flashcard_reviews").select("*").in("card_id", cardList.map((c) => c.id));
      const revMap = new Map((revs || []).map((r: any) => [r.card_id, r]));
      const now = Date.now();
      const q: { card: Flashcard; review: Review }[] = [];
      for (const c of cardList) {
        const r: any = revMap.get(c.id);
        if (!r) q.push({ card: c, review: { ease: 2.5, interval_days: 0, repetitions: 0, due_at: new Date().toISOString() } });
        else if (new Date(r.due_at).getTime() <= now) q.push({ card: c, review: r });
      }
      setQueue(q);
      setDone(q.length === 0);
    })();
  }, [slug, user]);

  async function grade(q: number) {
    if (!user || !queue[idx]) return;
    const { card, review } = queue[idx];
    const next = nextReview(review, q);
    await supabase.from("flashcard_reviews").upsert({
      user_id: user.id, card_id: card.id,
      ease: next.ease, interval_days: next.interval_days, repetitions: next.repetitions,
      due_at: next.due_at, last_reviewed_at: new Date().toISOString(),
    }, { onConflict: "user_id,card_id" });
    await supabase.rpc("bump_streak", { p_xp: 1 });
    setRevealed(false);
    setSelectedAnswer(null);
    if (idx + 1 >= queue.length) {
      setDone(true);
      toast.success("Session complete! 🎉");
    } else {
      setIdx(idx + 1);
    }
  }

  const current = queue[idx];
  const answerChoices = useMemo(() => {
    if (!current) return [];
    const distractors = allCards
      .filter((card) => card.id !== current.card.id && card.meaning && card.meaning !== current.card.meaning)
      .map((card) => card.meaning);
    return shuffle([current.card.meaning, ...shuffle([...new Set(distractors)]).slice(0, 3)]);
  }, [allCards, current]);

  if (!deck) return <p>Loading…</p>;
  if (done) return (
    <div className="max-w-md mx-auto text-center space-y-4 pt-12">
      <h2 className="text-2xl font-bold">All caught up! 🎉</h2>
      <p className="text-muted-foreground">No more cards due in this deck right now.</p>
      <Button asChild><Link to="/decks">Back to decks</Link></Button>
    </div>
  );

  function chooseAnswer(answer: string) {
    setSelectedAnswer(answer);
    setRevealed(true);
  }

  const answeredCorrectly = selectedAnswer === current.card.meaning;

  return (
    <div className="max-w-xl mx-auto space-y-4">
      <div className="text-sm text-muted-foreground flex justify-between">
        <Link to="/decks" className="hover:underline">← Decks</Link>
        <span>{idx + 1} / {queue.length}</span>
      </div>
      <div className="perspective">
        <div
          className={`flip-card relative min-h-[280px] w-full cursor-pointer ${revealed ? "rotate-y-180" : ""}`}
          onClick={() => setRevealed((v) => !v)}
        >
          {/* Front */}
          <Card className="absolute inset-0 backface-hidden min-h-[280px]">
            <CardContent className="p-8 h-full flex flex-col items-center justify-center text-center">
              <div className="text-5xl font-bold">{current.card.front}</div>
              {answerChoices.length > 1 ? (
                <div className="mt-8 grid w-full gap-2">
                  {answerChoices.map((choice) => (
                    <Button key={choice} type="button" variant="outline" onClick={(e) => { e.stopPropagation(); chooseAnswer(choice); }}>
                      {choice}
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground mt-6">Tap to flip</div>
              )}
            </CardContent>
          </Card>
          {/* Back */}
          <Card className="absolute inset-0 backface-hidden rotate-y-180 min-h-[280px] border-primary/40">
            <CardContent className="p-8 h-full flex flex-col items-center justify-center text-center space-y-3">
              {selectedAnswer && (
                <div className={`text-sm font-medium ${answeredCorrectly ? "text-primary" : "text-destructive"}`}>
                  {answeredCorrectly ? "Correct" : "Incorrect"}
                </div>
              )}
              {current.card.reading && <div className="text-xl text-muted-foreground">{current.card.reading}</div>}
              <div className="text-3xl font-semibold">{current.card.meaning}</div>
              {selectedAnswer && !answeredCorrectly && (
                <div className="text-sm text-muted-foreground">Your answer: {selectedAnswer}</div>
              )}
              {current.card.example_sentence && (
                <div className="pt-3 border-t w-full text-sm">
                  <div>{current.card.example_sentence}</div>
                  <div className="text-muted-foreground italic">{current.card.example_translation}</div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      {!revealed ? (
        <Button onClick={() => setRevealed(true)} className="w-full" size="lg">Show answer</Button>
      ) : (
        <div className="grid grid-cols-4 gap-2">
          <Button onClick={() => grade(1)} variant="destructive">Again</Button>
          <Button onClick={() => grade(3)} variant="secondary">Hard</Button>
          <Button onClick={() => grade(4)}>Good</Button>
          <Button onClick={() => grade(5)} variant="default" className="bg-green-600 hover:bg-green-700">Easy</Button>
        </div>
      )}
    </div>
  );
}
